/**
 * Checkout for an invited patient (PRA-031): missing intake answers are asked here, then the
 * existing checkout screen. The landing page and onboarding intake are skipped.
 */
import * as stylex from "@stylexjs/stylex";
import { useEffect, useState } from "react";
import type { IntakeAnswers, QuestionId } from "@lattice/core";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { Mark } from "@lattice/ui/Mark";
import { Status } from "@lattice/ui/Status";
import { loadIntake, saveIntakeAnswer } from "./api.ts";
import { Checkout } from "./Checkout.tsx";
import { QuestionBody } from "./start/QuestionBody.tsx";
import { PROMPT, visibleQuestions } from "./start/questions.ts";
function missingAtCheckout(answers: IntakeAnswers): QuestionId[] {
  return visibleQuestions(answers).filter(id => answers[id] === undefined);
}
export function CheckoutIntake() {
  const [answers, setAnswers] = useState<IntakeAnswers | null>(null);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  /*
    A refused read (signed out, or an account with no patient record) leaves the page to Checkout,
    which asks a signed-out visitor to sign in. Treating it as "nothing answered" would ask the
    first question, and every save would fail.
  */
  useEffect(() => {
    void loadIntake().then(loaded => {
      if (loaded !== null) setAnswers(loaded);
    });
  }, []);
  if (answers === null) return <Checkout />;
  const missing = missingAtCheckout(answers);
  const current = missing[0];
  if (current === undefined) return <Checkout />;
  const prompt = PROMPT[current];
  return <div className="x1ov3xa9 x1l0w1cl xzb3lse x1cikjda x1rf3zrh xbz0q3o x179o8vp xcmmui4" data-testid="checkout-intake">
      <div className="x19pysoa xvueqy4">
        <header className="x78zum5 x6s0dn4 x1wiri7k">
          <a href="/" className="x78zum5 x6s0dn4 x1173whf x1gg8mnh x10dbofl x1hl2dhg xqu410z" aria-label="Lattice home">
            <Mark size={22} />
            <span className="xzb3lse">Lattice</span>
          </a>
        </header>
        <main className="x78zum5 xdt5ytf x15agyyk xjh1bw0">
          <h1 id="question" className="x1ghz6dp x1u29x3t xqu410z x1ntj4s9 x1d3j7mx x1e5flnj x1w2vvpw">
            {prompt.heading}
          </h1>
          <p className="x1ghz6dp x1jty25b x1fzhlzt">{prompt.lead}</p>
          {failed ? <Status state="problem">We couldn't save that. Try again.</Status> : null}
          <QuestionBody labelledBy="question" id={current} answers={answers} onAnswer={(id, answer) => {
          if (saving) return;
          setSaving(true);
          setFailed(false);
          void saveIntakeAnswer(id, answer).then(ok => {
            setSaving(false);
            if (!ok) {
              setFailed(true);
              return;
            }
            setAnswers({
              ...answers,
              [id]: answer
            });
          });
        }} />
        </main>
      </div>
    </div>;
}